import React, { lazy } from "react";
import { Navigate, Outlet } from "react-router-dom";

// Layouts & Guards
import AppWrapper from "./AppWrapper";
import AuthGuard from "./components/guards/AuthGuard";
import PermissionGuard from "./components/guards/PermissionGuard";
import DashboardLayout from "./layouts/Dashboard";
import ErrorLayout from "./layouts/Error";
import PresentationLayout from "./layouts/Presentation";

// LWI-661 — small helper to wrap a route element in a permission gate.
// Pairs with PermissionGuard's default 403 fallback. Use it on every
// route that requires a specific permission to view.
const guard = (perm, el) => (
  <PermissionGuard permission={perm}>{el}</PermissionGuard>
);

import apiClient from "@utils/axiosInstance";
import { retryFailLoad } from "./utils/retryIfFailed.js";

// LWI-1748 — lazy so the purchase-orders module stays out of the main chunk.
const EditPurchaseOrderPage = lazy(
  retryFailLoad(() =>
    import("@modules/purchase-orders/components/EditPurchaseOrderPage").then(
      (m) => ({ default: m.EditPurchaseOrderPage })
    )
  )
);

const OracleLocations = lazy(
  retryFailLoad(() =>
    import("./pages/admin/OracleLocations/Dashboard/index.jsx")
  )
);
const OracleLocationDetails = lazy(
  retryFailLoad(() => import("./pages/admin/OracleLocations/Details/index.jsx"))
);
// Pages & Components
const Default = lazy(retryFailLoad(() => import("./pages/dashboards/Default")));
const Login = lazy(retryFailLoad(() => import("./pages/auth/Login")));
const ResetPassword = lazy(
  retryFailLoad(() => import("./components/auth/ResetPassword"))
);
const CompletePasswordReset = lazy(
  retryFailLoad(() => import("./components/auth/CompletePasswordReset"))
);
const Page404 = lazy(retryFailLoad(() => import("./pages/auth/Page404")));
const Logout = lazy(retryFailLoad(() => import("./pages/pages/Logout")));

const InboundShipments = lazy(
  retryFailLoad(() => import("./pages/shipments/Inbound/Dashboard"))
);
const InboundShipmentDetails = lazy(
  retryFailLoad(() => import("./pages/shipments/Inbound/Details"))
);

const ManualShipments = lazy(
  retryFailLoad(() => import("./pages/shipments/Manual/Dashboard"))
);
const MSRDetails = lazy(
  retryFailLoad(() => import("./pages/shipments/Manual/Details"))
);
const CreateMSR = lazy(
  retryFailLoad(() => import("./pages/shipments/Manual/Create"))
);
// LWI-1549 — CCI-style MSR wizard (lifecycle stepper + section tabs + per-section saves).
const MsrWizard = lazy(
  retryFailLoad(() => import("./pages/shipments/Manual/Wizard"))
);
const MsrDetailsRedirect = lazy(
  retryFailLoad(() => import("./modules/msr/MsrDetailsRedirect"))
);
const CreateISR = lazy(
  retryFailLoad(() => import("./pages/shipments/Inbound/Create"))
);
const Locations = lazy(
  retryFailLoad(() => import("./pages/locations/Dashboard"))
);
const LocationDetails = lazy(
  retryFailLoad(() => import("./pages/locations/Details"))
);

const Notifications = lazy(
  retryFailLoad(() => import("./pages/user/Notifications"))
);
const Profile = lazy(retryFailLoad(() => import("./pages/user/Profile")));
const ChangePassword = lazy(
  retryFailLoad(() => import("./pages/user/ChangePassword"))
);

const Parts = lazy(retryFailLoad(() => import("./pages/parts/Dashboard")));
const PartsDetails = lazy(retryFailLoad(() => import("./pages/parts/Details")));
const CreatePartForm = lazy(
  retryFailLoad(() => import("./pages/parts/Details/CreatePartForm"))
);
const PartsClassification = lazy(
  retryFailLoad(() => import("./pages/parts/Classifications"))
);

const PurchaseOrders = lazy(
  retryFailLoad(() => import("./pages/purchase-orders/Dashboard"))
);
const Programs = lazy(retryFailLoad(() => import("./pages/programs")));

const Licenses = lazy(
  retryFailLoad(() => import("./pages/license-control/Licenses"))
);
const EditLicense = lazy(
  retryFailLoad(() => import("./pages/license-control/EditLicense"))
);
const AddECCN = lazy(
  retryFailLoad(() => import("./pages/license-control/AddECCN"))
);
const AddUSML = lazy(
  retryFailLoad(() => import("./pages/license-control/AddUSML"))
);
const EditUSML = lazy(
  retryFailLoad(() => import("./pages/license-control/EditUSML"))
);
const ECCN = lazy(retryFailLoad(() => import("./pages/license-control/ECCN")));
const USML = lazy(retryFailLoad(() => import("./pages/license-control/USML")));
const CDNSuppliers = lazy(
  retryFailLoad(() => import("./pages/license-control/CDNSuppliers"))
);
const AddSupplier = lazy(
  retryFailLoad(() => import("./pages/license-control/AddSupplier"))
);
const Consignees = lazy(
  retryFailLoad(() => import("./pages/license-control/Consignees"))
);
const AddConsignee = lazy(
  retryFailLoad(() => import("./pages/license-control/AddConsignee"))
);

const AdminMIA = lazy(retryFailLoad(() => import("./pages/admin/MIA")));
const AdminMIAExceptions = lazy(
  retryFailLoad(() => import("./pages/admin/MIAExceptions"))
);
const AdminShipmentManagement = lazy(
  retryFailLoad(() => import("./pages/admin/ShipmentManagement"))
);
const AdminShipmentmentManagementDetails = lazy(
  retryFailLoad(() => import("./pages/admin/ShipmentManagement/Details"))
);

const AdminRoleManagement = lazy(
  retryFailLoad(() => import("./pages/admin/RoleManagement/Dashboard"))
);

const ImportMonitor = lazy(
  retryFailLoad(() => import("./pages/admin/ImportMonitor/Dashboard"))
);
const AdminRoleManagementDetails = lazy(
  retryFailLoad(() => import("./pages/admin/RoleManagement/Details"))
);

const AdminUserManagement = lazy(
  retryFailLoad(() => import("./pages/admin/UserManagement/Dashboard"))
);
const AdminUserManagementDetails = lazy(
  retryFailLoad(() => import("./pages/admin/UserManagement/Details"))
);

const AdminProjects = lazy(
  retryFailLoad(() => import("./pages/admin/Projects"))
);
// LWI-1637 — the old Program / Project Details URLs redirect into the workspace.
const AdminProgramDetailsRedirect = lazy(
  retryFailLoad(() =>
    import("./pages/admin/Projects/DetailsRedirect").then((m) => ({ default: m.ProgramDetailsRedirect }))
  )
);
const AdminProjectDetailsRedirect = lazy(
  retryFailLoad(() =>
    import("./pages/admin/Projects/DetailsRedirect").then((m) => ({ default: m.ProjectDetailsRedirect }))
  )
);

const AreaManagerManagement = lazy(
  retryFailLoad(() => import("./pages/admin/AreaManagers/Dashboard"))
);
const AreaManagerManagementDetails = lazy(
  retryFailLoad(() => import("./pages/admin/AreaManagers/Details"))
);

const Deliveries = lazy(
  retryFailLoad(() => import("./pages/shipments/Outbound/Deliveries/Dashboard"))
);
const DeliveryDetails = lazy(
  retryFailLoad(() => import("./pages/shipments/Outbound/Deliveries/Details"))
);

const SalesOrders = lazy(
  retryFailLoad(() =>
    import("./pages/shipments/Outbound/SalesOrders/Dashboard")
  )
);
const SalesOrderDetails = lazy(
  retryFailLoad(() => import("./pages/shipments/Outbound/SalesOrders/Details"))
);

const CustomsDashboard = lazy(
  retryFailLoad(() => import("./pages/customs/Dashboard"))
);
const CustomsInvoiceDetails = lazy(
  retryFailLoad(() => import("./pages/shipments/Manual/CustomsInvoice"))
);

// CCI Pages
const CCIList = lazy(retryFailLoad(() => import("./pages/supplier-cci")));
// LWI-1069 — chrome-free direct link that views a CCI PDF inline.
const CCIPdfView = lazy(retryFailLoad(() => import("./pages/supplier-cci/PdfView")));
const CCIReviewSuspended = lazy(retryFailLoad(() => import("./pages/supplier-cci/ReviewSuspended")));
// LWI-953 — read-only "View Approval" sibling of the suspended-review page.
const CCIViewApproval = lazy(retryFailLoad(() => import("./pages/supplier-cci/ViewApproval")));

// Invoice Pages
const InvoiceHeader = lazy(
  retryFailLoad(() => import("./pages/invoice/Header"))
);
const InvoiceBody = lazy(retryFailLoad(() => import("./pages/invoice/Body")));
const InvoiceFooter = lazy(
  retryFailLoad(() => import("./pages/invoice/Footer"))
);
const InvoiceAdditionalInfo = lazy(
  retryFailLoad(() => import("./pages/invoice/AdditionalInfo"))
);
const InvoiceReview = lazy(
  retryFailLoad(() => import("./pages/invoice/Review"))
);

const GlossaryOfTerms = lazy(
  retryFailLoad(() => import("./pages/support/glossary"))
);
const FAQPage = lazy(retryFailLoad(() => import("./pages/support/faq")));

const SsoCallback = lazy(
  retryFailLoad(() => import("./pages/auth/SsoCallback"))
);
const NoTenantAccess = lazy(
  retryFailLoad(() => import("./pages/auth/NoTenantAccess"))
);
const SelectTenant = lazy(
  retryFailLoad(() => import("./pages/auth/SelectTenant"))
);
const AcceptInvite = lazy(
  retryFailLoad(() => import("./pages/auth/AcceptInvite"))
);
const AuditLog = lazy(
  retryFailLoad(() => import("./pages/admin/AuditLog"))
);
const AccountLockoutAdmin = lazy(
  retryFailLoad(() => import("./pages/admin/AccountLockoutAdmin"))
);

const ReportBuilder = lazy(
  retryFailLoad(() => import("./pages/report-builder"))
);

// Analytics dashboards (LWI-1046)
const MiaTrends = lazy(
  retryFailLoad(() => import("./pages/analytics/MiaTrends"))
);
const ControlTower = lazy(
  retryFailLoad(() => import("./pages/analytics/ControlTower"))
);

const ReportNotifications = lazy(
  retryFailLoad(() => import("./pages/notifications"))
);

const KBIndex = lazy(retryFailLoad(() => import("./pages/knowledgeBase/Index/index.jsx")));
const KBView  = lazy(retryFailLoad(() => import("./pages/knowledgeBase/View/index.jsx")));
const KBEdit  = lazy(retryFailLoad(() => import("./pages/knowledgeBase/Edit/index.jsx")));


export default function getRoutes(tenantInfo) {
  return [
    {
      path: "/",
      element: <AppWrapper />,
      children: [
        {
          element: (
            <AuthGuard>
              <DashboardLayout />
            </AuthGuard>
          ),
          children: [
            { index: true, element: <Default /> },
            { path: "callback", element: <Default /> },
            { path: "dashboard/default", element: <Default /> },
            { path: "user/profile", element: <Profile /> },
            { path: "user/notifications", element: guard("notifications.view", <Notifications />) },
            { path: "support/glossary", element: guard("glossary.view", <GlossaryOfTerms />) },
            { path: "support/faq", element: guard("faq.view", <FAQPage />) },
            { path: "shipments/inbound", element: guard("isr.view", <InboundShipments />) },
            { path: "shipments/inbound/add", element: guard("isr.create", <CreateISR />) },
            { path: "shipments/inbound/:id/details", element: guard("isr.view", <InboundShipmentDetails />) },
            {
              path: "supplier-cci",
              element: <PermissionGuard permission="cci.view"><Outlet /></PermissionGuard>,
              children: [
                {
                  path: "",
                  element: <CCIList />,
                },
                {
                  path: "create-invoice",
                  handle: {
                    hideTitle: true,
                  },
                  children: [
                    {
                      path: "",
                      element: <Navigate to="header" replace />,
                    },
                    {
                      path: "header",
                      element: <InvoiceHeader />,
                    },
                    {
                      path: "body",
                      element: <InvoiceBody />,
                    },
                    {
                      path: "footer",
                      element: <InvoiceFooter />,
                    },
                    {
                      path: "additional-info",
                      element: <InvoiceAdditionalInfo />,
                    },
                    {
                      path: "review",
                      element: <InvoiceReview />,
                    },
                  ],
                },
                {
                  path: ":cciId",
                  children: [
                    {
                      path: "",
                      element: <Navigate to="header" replace />,
                    },
                    {
                      path: "header",
                      element: <InvoiceHeader />,
                    },
                    {
                      path: "body",
                      element: <InvoiceBody />,
                    },
                    {
                      path: "footer",
                      element: <InvoiceFooter />,
                    },
                    {
                      path: "additional-info",
                      element: <InvoiceAdditionalInfo />,
                      handle: {
                        breadcrumb: "Additional Info",
                        hideTitle: true,
                      },
                    },
                    {
                      path: "review",
                      element: <InvoiceReview />,
                    },
                    {
                      path: "review-suspended",
                      element: <CCIReviewSuspended />,
                    },
                    {
                      // LWI-953 — read-only view for approved CCIs.
                      path: "view-approval",
                      element: <CCIViewApproval />,
                    },
                  ],
                },
              ],
            },
            { path: "outbound/msr", element: guard("msr.view", <ManualShipments />), handle: { breadcrumb: "Manual Shipment Requests" } },
            // LWI-1549 — Create and Details are the wizard now. The old paths stay as
            // redirects so links keep working (`?tab=` maps to the wizard section).
            {
              path: "outbound/msr/add",
              element: (
                <Navigate to="/outbound/msr/wizard/new/shipment" replace />
              ),
            },
            {
              path: "outbound/msr/details/:id",
              element: <MsrDetailsRedirect />,
            },
            { path: "outbound/msr/wizard", element: <Navigate to="new/shipment" replace /> },
            {
              path: "outbound/msr/wizard/new/:section",
              element: guard("msr.create", <MsrWizard />),
              handle: { breadcrumb: "Create MSR" },
            },
            { path: "outbound/msr/wizard/:msrNumber/:section?", element: guard("msr.view", <MsrWizard />), handle: { breadcrumb: "MSR" } },
            // Classic pages kept unlinked as a fallback during the cutover; remove with the pages themselves.
            {
              path: "outbound/msr/classic/add",
              element: guard("msr.create", <CreateMSR />),
              handle: { breadcrumb: "Create MSR (classic)" },
            },
            {
              path: "outbound/msr/classic/details/:id",
              element: guard("msr.view", <MSRDetails />),
            },
            { path: "outbound/deliveries", element: guard("deliveries.view", <Deliveries />) },
            { path: "outbound/deliveries/details/:id", element: guard("deliveries.view", <DeliveryDetails />) },
            { path: "outbound/sales-orders", element: guard("so.view", <SalesOrders />) },
            { path: "outbound/sales-orders/details/:id", element: guard("so.view", <SalesOrderDetails />) },

            { path: "locations", element: guard("locations.view", <Locations />) },
            { path: "locations/:id/details", element: guard("locations.view", <LocationDetails />) },
            { path: "customs", element: guard("customs.view", <CustomsDashboard />) },
            { path: "customs/:id", element: guard("customs.view", <CustomsInvoiceDetails />) },
            { path: "parts", element: guard("parts.view", <Parts />) },
            { path: "parts/create", element: guard("parts.create", <CreatePartForm />) },
            { path: "parts/:id/details", element: guard("parts.view", <PartsDetails />) },
            { path: "parts/classifications", element: guard("parts.view", <PartsClassification />) },

            { path: "purchase-orders", element: guard("lcs_po.view", <PurchaseOrders />) },
            { path: "report-builder", element: guard("report.view", <ReportBuilder />) },
            // MIA Trends hosts both the Trends and (LWI-1120) Status tabs,
            // each self-gated — any-of grants access to the shell.
            {
              path: "analytics/mia-trends",
              element: (
                <PermissionGuard
                  anyOf={["analytics.mia_trends.view", "analytics.mia_status.view"]}
                >
                  <MiaTrends />
                </PermissionGuard>
              ),
            },
            { path: "analytics/control-tower", element: guard("analytics.control_tower.view", <ControlTower />) },

            { path: "notifications", element: guard("notifications.view", <ReportNotifications />) },
            { path: "admin/my-exceptions", element: guard("mia_exception.view", <AdminMIAExceptions />) },
            { path: "admin/mia", element: guard("mia.view", <AdminMIA />) },
            { path: "admin/programs/:id/details", element: guard("program.view", <AdminProgramDetailsRedirect />) },
            { path: "admin/projects", element: guard("program.view", <AdminProjects />) },
            { path: "admin/projects/:id/details", element: guard("program.view", <AdminProjectDetailsRedirect />) },

            { path: "admin/shipmentmanagement", element: guard("shipment_admin.view", <AdminShipmentManagement />) },
            { path: "admin/shipmentmanagement/:id/details", element: guard("shipment_admin.view", <AdminShipmentmentManagementDetails />) },

            { path: "licence-control/purchase-orders", element: guard("lcs_po.view", <PurchaseOrders />) },
            { path: "licence-control/purchase-orders/edit/:poNumber", element: guard("lcs_po.view", <EditPurchaseOrderPage />) },
            { path: "licence-control/programs", element: guard("lcs_program.view", <Programs />) },
            { path: "licence-control/licenses", element: guard("lcs_license.view", <Licenses />) },
            { path: "licence-control/licenses/:id/edit", element: guard("lcs_license.edit", <EditLicense />) },
            { path: "licence-control/eccn", element: guard("lcs_eccn.view", <ECCN />) },
            { path: "licence-control/eccn/add", element: guard("lcs_eccn.create", <AddECCN />) },
            { path: "licence-control/usml", element: guard("lcs_usml.view", <USML />) },
            { path: "licence-control/usml/add", element: guard("lcs_usml.create", <AddUSML />) },
            { path: "licence-control/usml/:id/edit", element: guard("lcs_usml.edit", <EditUSML />) },
            { path: "licence-control/cdn-suppliers", element: guard("lcs_supplier.view", <CDNSuppliers />) },
            { path: "licence-control/cdn-suppliers/add", element: guard("lcs_supplier.create", <AddSupplier />) },
            { path: "licence-control/cdn-suppliers/edit/:supplierId", element: guard("lcs_supplier.edit", <AddSupplier />) },
            // LWI-1165 — Consignees (share the lcs_supplier permissions for now).
            { path: "licence-control/consignees", element: guard("lcs_supplier.view", <Consignees />) },
            { path: "licence-control/consignees/add", element: guard("lcs_supplier.create", <AddConsignee />) },
            { path: "licence-control/consignees/edit/:consigneeId", element: guard("lcs_supplier.edit", <AddConsignee />) },

            { path: "admin/area-managers", element: guard("area_manager.view", <AreaManagerManagement />) },
            { path: "admin/area-managers/:id/details", element: guard("area_manager.view", <AreaManagerManagementDetails />) },
            { path: "admin/oracle-locations", element: guard("oracle_location.view", <OracleLocations />) },
            { path: "admin/oracle-locations/:id/details", element: guard("oracle_location.view", <OracleLocationDetails />) },
            { path: "admin/roles", element: guard("roles.view", <AdminRoleManagement />) },
            { path: "admin/roles/:id/details", element: guard("roles.view", <AdminRoleManagementDetails />) },

            { path: "integration/import-monitor", element: guard("monitor.view", <ImportMonitor />) },

            { path: "admin/users", element: guard("users.view", <AdminUserManagement />) },
            { path: "admin/users/:id", element: guard("users.view", <AdminUserManagementDetails />) },
            { path: "admin/account-lockouts", element: guard("users.lockouts.view", <AccountLockoutAdmin />) },
            { path: "admin/audit-log", element: guard("audit.view", <AuditLog />), handle: { breadcrumb: "Audit Log" } },
            { path: "account/change-password", element: <ChangePassword /> },
            { path: "knowledge-base",          element: guard("kb.view", <KBIndex />) },
            { path: "knowledge-base/new",      element: guard("kb.create", <KBEdit />) },
            { path: "knowledge-base/:id/edit", element: <PermissionGuard anyOf={["kb.edit.any", "kb.edit.own"]}><KBEdit /></PermissionGuard> },
            { path: "knowledge-base/:id",      element: guard("kb.view", <KBView />) },
          ],
        },

        {
          // LWI-1069 — direct link to a CCI PDF. Inside AuthGuard (deep
          // link survives login via LWI-592) but outside DashboardLayout
          // so the page is just the PDF, no app chrome.
          path: "cci-pdf/:cciNumber",
          element: (
            <AuthGuard>
              <CCIPdfView />
            </AuthGuard>
          ),
        },

        { path: "login", element: <Login /> },
        { path: "select-tenant", element: <SelectTenant /> },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "complete-password-reset", element: <CompletePasswordReset /> },
        { path: "no-tenant-access", element: <NoTenantAccess /> },
        { path: "/sso/callback", element: <SsoCallback /> },
        { path: "/accept-invite", element: <AcceptInvite /> },
        {
          path: "logout",
          element: <PresentationLayout />,
          children: [{ index: true, element: <Logout /> }],
        },
        {
          element: <ErrorLayout />,
          children: [{ path: "*", element: <Page404 /> }],
        },
      ],
    },
  ];
}
